import { Icon } from '@chakra-ui/react';
import { BiExport } from 'react-icons/bi';
import { IoMdGitCompare } from 'react-icons/io';
import { LuActivity, LuCopy, LuGitCommitHorizontal, LuPencil, LuSettings, LuTag, LuTrash2, LuUsers, LuArchive, LuArchiveRestore } from 'react-icons/lu';
import { MdAccountTree } from 'react-icons/md';
import { InferenceAlgorithm } from '@/services/bayesianApi/models';
import { suggestNextReleaseBranchName } from '@/services/models/networkVersion';
import { CopyKind } from './copyKind';
import { decideCopyName } from './decideCopyName';
import { InputMenuItem, Option, SelectMenuItem, SimpleMenuItem } from './VirtualizedSelectPopover';
export const generateRenameMenuItem = (action: ({
  id,
  name
}: {
  id: string;
  name: string;
}) => unknown): InputMenuItem => ({
  menuType: 'input',
  props: {
    actionTitle: 'Rename',
    defaultInputValue: option => option.name,
    onSubmit: (input, option) => action({
      id: option.id,
      name: input
    }),
    modalTitle: 'Please rename your network',
    preventSavingDefaultValue: true,
    icon: <LuPencil />
  }
});
export const generateVersionMenuItem = (action: ({
  id,
  version
}: {
  id: string;
  version: string;
}) => unknown): InputMenuItem => ({
  menuType: 'input',
  props: {
    actionTitle: 'Set version',
    defaultInputValue: option => option.version ?? '',
    onSubmit: (input, option) => action({
      id: option.id,
      version: input
    }),
    modalTitle: 'Set network version (leave empty to clear)',
    preventSavingDefaultValue: true,
    allowEmpty: true,
    icon: <LuTag />
  }
});
export const generateDuplicateMenuItem = (action: ({
  id,
  name,
  isReleaseBranch,
  copyKind
}: {
  id: string;
  name: string;
  isReleaseBranch?: boolean;
  copyKind?: CopyKind;
}) => void, existingNames: Array<string>, {
  allowRefBranch
}: {
  allowRefBranch: boolean;
}): InputMenuItem => ({
  menuType: 'input',
  props: {
    actionTitle: 'Duplicate',
    defaultInputValue: option => decideCopyName(option.name, existingNames),
    // KBE-884: a duplicate no longer copies the source's history — every copy
    // starts with a fresh audit log (lineage lives in the fork commit; ref/delta
    // branches already never copied it). Only the system backup keeps history.
    onSubmit: (input, option, isReleaseBranch, copyKind) => action({
      id: option.id,
      name: input,
      isReleaseBranch,
      copyKind
    }),
    modalTitle: 'Please name your copy',
    icon: <LuCopy />,
    deltaConfig: {
      label: 'Create as delta branch'
    },
    // A ref branch can only be cut from a protected ref (the master or a
    // release branch), and is never the default (KBE-981).
    refBranchConfig: allowRefBranch ? {
      label: 'Create as ref branch (experimental)',
      warning: 'Merge from a ref branch only once, structure and assumption sets together. If the source changed in the meantime, a later merge can undo those changes.'
    } : undefined,
    // A release branch can only be cut from the master; its name is computed
    // automatically (next minor line, e.g. 3.24.X). For any other source the
    // release-branch option is not offered. See KBE-685.
    intentConfig: {
      releaseBranchName: option => option.isMain ? suggestNextReleaseBranchName(existingNames) : null
    }
  }
});
export const generateDuplicateViewMenuItem = (action: ({
  id,
  name
}: {
  id: string;
  name: string;
}) => unknown, existingNames: Array<string>): InputMenuItem => ({
  menuType: 'input',
  props: {
    actionTitle: 'Duplicate',
    defaultInputValue: option => decideCopyName(option.name, existingNames),
    onSubmit: (input, option) => action({
      id: option.id,
      name: input
    }),
    modalTitle: 'Please name your copy',
    icon: <LuCopy />
  }
});
export const generateReleaseMenuItem = ({
  action,
  disabled
}: {
  // Opens the release dialog (name + required version, KBE-544); the actual
  // release mutation fires from there, not from this menu item.
  action: ({
    id,
    name
  }: {
    id: string;
    name: string;
  }) => void;
  disabled: boolean;
}): SimpleMenuItem => ({
  menuType: 'simple',
  props: {
    title: 'Release',
    onClick: option => action({
      id: option.id,
      name: option.name
    }),
    icon: <LuCopy />,
    disabled
  }
});
export const generateDeleteMenuItem = (action: ({
  id,
  name
}: {
  id: string;
  name: string;
}) => void): SimpleMenuItem => ({
  menuType: 'simple',
  props: {
    title: 'Delete',
    onClick: option => action({
      id: option.id,
      name: option.name
    }),
    icon: <LuTrash2 color="red" />
  }
});
export const generateExportMenuItem = (action: ({
  id,
  name
}: {
  id: string;
  name: string;
}) => void): SimpleMenuItem => ({
  menuType: 'simple',
  props: {
    title: 'Export',
    onClick: option => {
      action({
        ...option
      });
    },
    icon: <BiExport />
  }
});
export const generateReportMenuItem = (action: ({
  id,
  name,
  inferenceAlgorithm
}: {
  id: string;
  name: string;
  inferenceAlgorithm: InferenceAlgorithm;
}) => void): SimpleMenuItem => ({
  menuType: 'simple',
  props: {
    title: 'Generate report',
    onClick: option => {
      action({
        ...option,
        inferenceAlgorithm: option.inferenceAlgorithm as InferenceAlgorithm
      });
    },
    icon: <Icon as={MdAccountTree} />
  }
});
export const generateCompareMenuItem = (action: ({
  id,
  name
}: {
  id: string;
  name: string;
}) => void): SimpleMenuItem => ({
  menuType: 'simple',
  props: {
    title: 'Compare to...',
    onClick: option => action({
      id: option.id,
      name: option.name
    }),
    icon: <IoMdGitCompare />
  }
});
export const generateAlgorithmMenuItem = (action: ({
  id,
  name,
  algorithm
}: {
  id: string;
  name: string;
  algorithm: InferenceAlgorithm;
}) => Promise<unknown>): SelectMenuItem => ({
  menuType: 'select',
  props: {
    title: (option: Option) => `Change inference (${option.inferenceAlgorithm})`,
    icon: <LuSettings />,
    modalTitle: 'Select inference algorithm',
    options: Object.values(InferenceAlgorithm),
    currentValue: option => option.inferenceAlgorithm as string,
    onSubmit: (value, option) => action({
      id: option.id,
      name: option.name,
      algorithm: value as InferenceAlgorithm
    })
  }
});

// KBE-769: opens the network's default-transition-function dialog. The mutation
// fires from there (it needs two fields), not from this menu item.
export const generateTransitionDefaultsMenuItem = (action: ({
  id,
  name
}: {
  id: string;
  name: string;
}) => void): SimpleMenuItem => ({
  menuType: 'simple',
  props: {
    title: 'Transition defaults',
    onClick: option => action({
      id: option.id,
      name: option.name
    }),
    icon: <LuActivity />
  }
});
export const generateViewCommitsMenuItem = (action: ({
  id
}: {
  id: string;
}) => void): SimpleMenuItem => ({
  menuType: 'simple',
  props: {
    title: 'View commits',
    onClick: option => action({
      id: option.id
    }),
    icon: <LuGitCommitHorizontal />
  }
});
export const generateRetireMenuItem = (action: ({
  id,
  name
}: {
  id: string;
  name: string;
}) => void): SimpleMenuItem => ({
  menuType: 'simple',
  props: {
    title: 'Retire',
    onClick: option => action({
      id: option.id,
      name: option.name
    }),
    icon: <LuArchive />
  }
});
export const generateUnretireMenuItem = (action: ({
  id,
  name
}: {
  id: string;
  name: string;
}) => void): SimpleMenuItem => ({
  menuType: 'simple',
  props: {
    title: 'Unretire',
    onClick: option => action({
      id: option.id,
      name: option.name
    }),
    icon: <LuArchiveRestore />
  }
});
export const generateApplyBasePopulationMenuItem = (action: ({
  id,
  name
}: {
  id: string;
  name: string;
}) => Promise<void>): SimpleMenuItem => ({
  menuType: 'simple',
  props: {
    title: 'Apply Base Population',
    onClick: option => void action({
      id: option.id,
      name: option.name
    }),
    icon: <LuUsers />
  }
});